import { supabase } from '../../lib/supabase';
import type { AiReviewFile, AiReviewItem, AiReviewStatus } from '../../components/AiReviewReport';
import type { ManualRegistrationData } from './types';
import { calculateLoanDetails } from './types';
import { extractAudioWavBase64, extractVideoFrames } from './videoMedia';

export type AiReviewState =
  | { signature: string; phase: 'running' }
  | { signature: string; phase: 'done'; reviewId: string; overall: AiReviewStatus; items: AiReviewItem[]; files: AiReviewFile[] }
  | { signature: string; phase: 'failed'; reviewId: string | null; message: string };

const CLIENT_TIMEOUT_MS = 170000;

export function collectFiles(data: ManualRegistrationData) {
  return data.documents.flatMap(d => d.urls.map((url, i) => ({ url, file: d.files[i] as File | undefined })));
}

export function filesSignature(data: ManualRegistrationData): string {
  return collectFiles(data).map(f => f.url).join('|');
}

export async function runContractAiReview(
  data: ManualRegistrationData,
  contractTempId: string,
): Promise<Omit<Extract<AiReviewState, { phase: 'done' }>, 'signature'> | Omit<Extract<AiReviewState, { phase: 'failed' }>, 'signature'>> {
  const files = collectFiles(data);
  const details = calculateLoanDetails(data.loan.amount, data.loan.installments, data.loan.interestRate);

  const videos = await Promise.all(
    files.map(async (f, fileIndex) => {
      if (!f.file || !f.file.type.startsWith('video/')) return null;
      const [frames, audioWavBase64] = await Promise.all([extractVideoFrames(f.file), extractAudioWavBase64(f.file)]);
      return { fileIndex, frames, audioWavBase64 };
    }),
  );

  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { phase: 'failed', reviewId: null, message: 'Sessão expirada. Entre novamente no sistema.' };

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
  try {
    const resp = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/contract-ai-review`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({
        contractTempId,
        client: { name: data.personal.name, cpf: data.personal.cpf, phone: data.personal.phone },
        residentialAddress: data.enderecoResidencial,
        commercialAddress: data.enderecoComercial,
        loan: {
          amount: parseFloat(data.loan.amount) || 0,
          installments: parseInt(data.loan.installments) || 0,
          installmentAmount: details.installmentAmount,
        },
        contacts: data.contacts.map(c => ({ nome: c.nome, telefone: c.telefone, relacao: c.relacao })),
        files: files.map(f => ({
          url: f.url,
          name: f.file?.name || 'arquivo',
          type: f.file?.type || '',
          lastModified: f.file?.lastModified,
        })),
        videos: videos.filter(Boolean),
      }),
      signal: controller.signal,
    });
    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json) {
      return { phase: 'failed', reviewId: null, message: json?.error || 'Não foi possível concluir a revisão automática.' };
    }
    if (json.status === 'completed' && typeof json.reviewId === 'string' && Array.isArray(json.items) && json.overall) {
      return {
        phase: 'done',
        reviewId: json.reviewId,
        overall: json.overall,
        items: json.items,
        files: Array.isArray(json.files) ? json.files : [],
      };
    }
    return {
      phase: 'failed',
      reviewId: typeof json.reviewId === 'string' ? json.reviewId : null,
      message: json.error || 'Não foi possível concluir a revisão automática.',
    };
  } catch (err) {
    const aborted = err instanceof DOMException && err.name === 'AbortError';
    return { phase: 'failed', reviewId: null, message: aborted ? 'A revisão automática demorou demais.' : 'Falha de conexão na revisão automática.' };
  } finally {
    clearTimeout(timer);
  }
}
